import { useState, useRef, useEffect, useMemo } from "react";
import { motion, useAnimationControls } from "framer-motion";

/**
 * Premium Pop Panda spin wheel.
 * Props:
 *  - segments: [{ id, label, color, image_url, type, value }]
 *  - spinning: bool
 *  - targetIndex: number | null
 *  - onSpin: () => void
 *  - onSpinComplete: () => void
 *  - disabled: bool
 *  - themeColor, accentColor: string
 *  - soundOn: bool
 *  - mood: "waiting" | "excited" | "celebrating" | "waving"
 */
export default function SpinWheel({
  segments, spinning, targetIndex, onSpin, onSpinComplete,
  disabled, themeColor = "#FFC93C", accentColor = "#FF4F5A", soundOn = true, mood = "waiting",
}) {
  const [rotation, setRotation] = useState(0);
  const audioCtxRef = useRef(null);
  const tickTimerRef = useRef(null);
  const wheelRef = useRef(null);
  const pointerControls = useAnimationControls();

  const n = segments.length || 1;
  const segAngle = 360 / n;
  const SIZE = 460;
  const R = 210;
  const CENTER = SIZE / 2;

  const wedges = useMemo(() => segments.map((s, i) => ({
    ...s,
    startAngle: i * segAngle - 90,
    endAngle: (i + 1) * segAngle - 90,
    midAngle: i * segAngle - 90 + segAngle / 2,
  })), [segments, segAngle]);

  // ------- Sound -------
  const getAudioCtx = () => {
    if (!soundOn) return null;
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      return ctx;
    } catch { return null; }
  };

  const tick = () => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(1400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(700, ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
    osc.connect(gain).connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.08);
  };

  const winFanfare = () => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.11);
      gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + i * 0.11 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.11 + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.11);
      osc.stop(ctx.currentTime + i * 0.11 + 0.25);
    });
  };

  // Tick + pointer wobble during spin
  useEffect(() => {
    if (spinning) {
      const start = Date.now();
      const schedule = () => {
        tick();
        pointerControls.start({ rotate: [-10, 10, 0], transition: { duration: 0.08 } });
        const elapsed = Date.now() - start;
        if (elapsed > 5100) return;
        const nextDelay = 45 + Math.pow(elapsed / 5000, 2) * 260;
        tickTimerRef.current = setTimeout(schedule, nextDelay);
      };
      schedule();
    } else if (tickTimerRef.current) {
      clearTimeout(tickTimerRef.current);
      pointerControls.start({ rotate: 0 });
    }
    return () => { if (tickTimerRef.current) clearTimeout(tickTimerRef.current); };
    // eslint-disable-next-line
  }, [spinning]);

  // Rotation target when spinning starts
  useEffect(() => {
    if (spinning && typeof targetIndex === "number") {
      const targetMid = targetIndex * segAngle + segAngle / 2;
      const finalRelative = 360 - (targetMid % 360);
      const extraSpins = 6;
      setRotation((prev) => Math.floor(prev / 360) * 360 + extraSpins * 360 + finalRelative + 360);
    }
    // eslint-disable-next-line
  }, [spinning, targetIndex]);

  const handleAnimationComplete = () => {
    if (spinning) {
      winFanfare();
      onSpinComplete?.();
    }
  };

  const polarToCartesian = (angle, radius) => {
    const rad = (angle * Math.PI) / 180;
    return { x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad) };
  };

  const wedgePath = (start, end) => {
    const p1 = polarToCartesian(start, R);
    const p2 = polarToCartesian(end, R);
    const largeArc = end - start <= 180 ? 0 : 1;
    return `M ${CENTER} ${CENTER} L ${p1.x} ${p1.y} A ${R} ${R} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
  };

  return (
    <div className="relative flex flex-col items-center">
      {/* Breathing glow */}
      <motion.div
        className="absolute inset-0 grid place-items-center pointer-events-none"
        animate={{ opacity: [0.3, 0.55, 0.3], scale: [1, 1.05, 1] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-[560px] h-[560px] rounded-full" style={{ background: `radial-gradient(circle at center, ${themeColor}66, transparent 60%)` }} />
      </motion.div>

      {/* Slowly rotating conic ring */}
      <motion.div
        className="absolute inset-0 grid place-items-center pointer-events-none"
        animate={{ rotate: 360 }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        <div className="w-[520px] h-[520px] rounded-full opacity-30" style={{ background: `conic-gradient(from 0deg, ${themeColor}, ${accentColor}, ${themeColor}, ${accentColor}, ${themeColor})`, maskImage: "radial-gradient(circle, transparent 45%, black 47%, black 50%, transparent 52%)", WebkitMaskImage: "radial-gradient(circle, transparent 45%, black 47%, black 50%, transparent 52%)" }} />
      </motion.div>

      {/* Floating tiny icons orbiting the wheel */}
      {["🍭", "🍓", "🍫", "🪙", "🐼", "✨"].map((emoji, i) => (
        <motion.span
          key={i}
          className="absolute text-2xl pointer-events-none select-none"
          style={{
            left: `calc(50% + ${Math.cos((i / 6) * Math.PI * 2) * 260}px - 12px)`,
            top: `calc(50% + ${Math.sin((i / 6) * Math.PI * 2) * 260}px - 12px)`,
          }}
          animate={{ y: [-6, 6, -6], rotate: [-8, 8, -8], opacity: [0.65, 1, 0.65] }}
          transition={{ duration: 3 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
        >{emoji}</motion.span>
      ))}

      {/* Wheel */}
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        {/* Pointer at top */}
        <motion.div className="absolute left-1/2 -translate-x-1/2 -top-3 z-20 pointer-events-none" animate={pointerControls} style={{ transformOrigin: "50% 100%" }}>
          <svg width="58" height="70" viewBox="0 0 58 70">
            <defs>
              <linearGradient id="ptrGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={accentColor} />
                <stop offset="100%" stopColor="#7a1616" />
              </linearGradient>
              <radialGradient id="ptrShine" cx="35%" cy="30%" r="45%">
                <stop offset="0%" stopColor="#fff" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
            </defs>
            <path d="M29 66 L4 8 Q29 -4 54 8 Z" fill="url(#ptrGrad)" stroke="#2B2B2B" strokeWidth="2.5" strokeLinejoin="round" />
            <path d="M29 66 L4 8 Q29 -4 54 8 Z" fill="url(#ptrShine)" />
            <circle cx="29" cy="18" r="5" fill="#fff" />
            <circle cx="29" cy="18" r="2" fill={accentColor} />
          </svg>
        </motion.div>

        <motion.div
          ref={wheelRef}
          animate={{ rotate: rotation }}
          transition={{ duration: 5, ease: [0.12, 0.7, 0.14, 1] }}
          onAnimationComplete={handleAnimationComplete}
          style={{ width: SIZE, height: SIZE, transformOrigin: "50% 50%" }}
        >
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            <defs>
              <radialGradient id="wheelInner" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3a3a3a" />
                <stop offset="100%" stopColor="#141414" />
              </radialGradient>
              {wedges.map((w) => (
                <radialGradient key={`grad-${w.id}`} id={`wg-${w.id}`} cx="50%" cy="50%" r="80%">
                  <stop offset="0%" stopColor={lighten(w.color, 0.18)} />
                  <stop offset="100%" stopColor={w.color} />
                </radialGradient>
              ))}
              <radialGradient id="wheelShine" cx="35%" cy="30%" r="55%">
                <stop offset="0%" stopColor="#fff" stopOpacity="0.35" />
                <stop offset="60%" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
              <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#000" floodOpacity="0.45" />
              </filter>
            </defs>

            <circle cx={CENTER} cy={CENTER} r={R + 14} fill="#1a1a1a" filter="url(#softShadow)" />
            <circle cx={CENTER} cy={CENTER} r={R + 8} fill="none" stroke={themeColor} strokeWidth="3" />

            {wedges.map((w) => (
              <g key={w.id}>
                <path d={wedgePath(w.startAngle, w.endAngle)} fill={`url(#wg-${w.id})`} stroke="#2B2B2B" strokeWidth="2" />
                <g transform={`rotate(${w.midAngle + 90}, ${CENTER}, ${CENTER})`}>
                  <text
                    x={CENTER}
                    y={CENTER - R * 0.78}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="32"
                    style={{ fontFamily: "'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif" }}
                  >
                    {emojiFor(w)}
                  </text>
                  <text
                    x={CENTER}
                    y={CENTER - R * 0.52}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={contrastText(w.color)}
                    fontSize={w.label.length > 14 ? 12 : 13}
                    fontWeight="700"
                    style={{ fontFamily: "system-ui, sans-serif", letterSpacing: "0.02em" }}
                  >
                    {truncateLabel(w.label)}
                  </text>
                  {w.value && w.type === "coins" && (
                    <text x={CENTER} y={CENTER - R * 0.35} textAnchor="middle" fill={contrastText(w.color)} fontSize="16" fontWeight="800" style={{ fontFamily: "system-ui,sans-serif" }}>
                      +{w.value}
                    </text>
                  )}
                </g>
                <circle cx={polarToCartesian(w.endAngle, R + 3).x} cy={polarToCartesian(w.endAngle, R + 3).y} r="3.5" fill="#FFF8F0" opacity="0.9" />
                <circle cx={polarToCartesian(w.endAngle, R + 3).x} cy={polarToCartesian(w.endAngle, R + 3).y} r="1.5" fill={themeColor} />
              </g>
            ))}

            <circle cx={CENTER} cy={CENTER} r={R} fill="url(#wheelShine)" pointerEvents="none" />
            <circle cx={CENTER} cy={CENTER} r="52" fill="url(#wheelInner)" stroke={themeColor} strokeWidth="4" />
          </svg>
        </motion.div>

        {/* Center SPIN button — panda */}
        <motion.button
          type="button"
          onClick={onSpin}
          disabled={disabled || spinning}
          data-testid="spin-button"
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 rounded-full h-28 w-28 grid place-items-center transition-shadow shadow-2xl overflow-hidden ${
            disabled || spinning ? "cursor-not-allowed" : "cursor-pointer"
          }`}
          style={{
            background: `radial-gradient(circle at 30% 30%, ${lighten(themeColor, 0.28)}, ${themeColor})`,
            boxShadow: `0 12px 28px rgba(0,0,0,0.45), inset 0 -4px 12px rgba(0,0,0,0.2), 0 0 0 4px #1a1a1a`,
          }}
          animate={
            spinning ? { scale: [1, 0.95, 1] } :
            disabled ? { scale: 1 } :
            { scale: [1, 1.05, 1] }
          }
          transition={
            spinning ? { duration: 0.4, repeat: Infinity } :
            { duration: 2, repeat: Infinity, ease: "easeInOut" }
          }
          whileTap={{ scale: 0.9 }}
        >
          <MiniPanda spinning={spinning} mood={mood} />
          <span className="absolute bottom-1.5 text-[10px] font-bold tracking-[0.28em] text-brand-charcoal/85" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.5)" }}>
            {spinning ? "SPINNING" : disabled ? "LOCKED" : "SPIN"}
          </span>
        </motion.button>
      </div>
    </div>
  );
}

function MiniPanda({ spinning, mood }) {
  return (
    <motion.svg
      viewBox="0 0 100 100"
      className="w-16 h-16"
      animate={spinning ? { rotate: [0, -8, 8, -8, 0] } : mood === "celebrating" ? { y: [0, -4, 0] } : {}}
      transition={{ duration: 0.5, repeat: Infinity }}
    >
      <circle cx="26" cy="26" r="14" fill="#111" />
      <circle cx="74" cy="26" r="14" fill="#111" />
      <ellipse cx="50" cy="56" rx="38" ry="34" fill="#fff" stroke="#2B2B2B" strokeWidth="2" />
      <ellipse cx="38" cy="52" rx="8" ry="10" fill="#111" transform="rotate(-10 38 52)" />
      <ellipse cx="62" cy="52" rx="8" ry="10" fill="#111" transform="rotate(10 62 52)" />
      <circle cx="38" cy="52" r="2.5" fill="#fff" />
      <circle cx="62" cy="52" r="2.5" fill="#fff" />
      <ellipse cx="50" cy="66" rx="4" ry="3" fill="#111" />
      <path d={spinning ? "M 42 76 Q 50 72 58 76" : "M 42 74 Q 50 82 58 74"} stroke="#2B2B2B" strokeWidth="2" fill="none" strokeLinecap="round" />
    </motion.svg>
  );
}

function emojiFor(w) {
  const type = (w.type || "").toLowerCase();
  const label = (w.label || "").toLowerCase();
  if (type === "coins") return "🪙";
  if (type === "popsicle") return "🍭";
  if (type === "popcorn") return "🍿";
  if (type === "discount") return "🏷️";
  if (type === "bogo") return "🎁";
  if (type === "mystery") return "🎲";
  if (type === "festival") return "🎊";
  if (type === "nothing") return "🐼";
  if (label.includes("chocolate")) return "🍫";
  if (label.includes("strawberry")) return "🍓";
  return "✨";
}

function lighten(hex, amt) {
  try {
    const c = hex.replace("#", "");
    const r = Math.min(255, Math.round(parseInt(c.substring(0, 2), 16) + 255 * amt));
    const g = Math.min(255, Math.round(parseInt(c.substring(2, 4), 16) + 255 * amt));
    const b = Math.min(255, Math.round(parseInt(c.substring(4, 6), 16) + 255 * amt));
    return `rgb(${r},${g},${b})`;
  } catch { return hex; }
}

function contrastText(hex) {
  try {
    const c = hex.replace("#", "");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.55 ? "#2B2B2B" : "#FFF8F0";
  } catch { return "#2B2B2B"; }
}

function truncateLabel(s) {
  if (!s) return "";
  if (s.length <= 16) return s;
  return s.slice(0, 14) + "…";
}
